import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { hfpInstrumentForRecord, hasStoredHfpInstrument } from '../../utils/referralStatus';
import { profileSharedWithClinicians, PROFILE_NOT_SHARED_LINE } from '../../utils/profileSharing';
import { useParams, useNavigate } from 'react-router-dom';
import { db, functions } from '../../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../hooks/useAuth';
import RmsTopNavBar from '../../components/provider/RmsTopNavBar';
import HfpV2Report from '../../components/hfp/HfpV2Report';
import buildHfpV3ReportModel from '../../utils/hfpV3ReportAdapter';
import HfpReportApp from '../../components/hfpReport/HfpReportApp';
import HfpV4ClinicianView from '../../components/hfp/HfpV4ClinicianView';
import { apiGetHfpV4Report } from '../../utils/rmsApi';
import { resolvePronounSet } from '../../components/hfpReport/pronouns';

/**
 * RmsHfpProfilePage
 * -----------------
 * The clinician's "Service User View" of a patient's Holistic Functional
 * Profile, at /rms/provider/patient/:patientRecordId/hfp.
 *
 * The point of this page is to show a clinician exactly what the family sees,
 * so records scored under HFP v3 render the SAME redesigned report experience
 * (HfpReportApp) that RmsServiceUserHfpPage gives a parent or school, from the
 * same persisted `domains.hfpV3.scoring` via the same adapter and the same
 * narratives callable. If the two ever diverge the button stops telling the
 * truth, so any change here belongs on both pages.
 *
 * Records still on v2 keep the legacy renderer in the clinician presentation
 * (HfpV2Report with clinicianView), inside the provider shell: a fixed-height
 * layout so the report's navigation and colour key stay put while only the
 * content scrolls, with the not-a-diagnosis disclaimer always visible.
 */
export default function RmsHfpProfilePage() {
  const navigate = useNavigate();
  const { patientRecordId } = useParams();
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;

  const [childName, setChildName] = useState('the child');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // HFP v3 (TD-200): records scored under v3 render the deterministic family
  // report straight from the persisted payload — no report callable involved.
  const [recordLoaded, setRecordLoaded] = useState(false);
  const [hfpV3Scoring, setHfpV3Scoring] = useState(null);
  // The record's HFP instrument (TDI-414) and whether it is stored on the
  // record; records created before the setting resolve it from the old rule.
  const [instrument, setInstrument] = useState('v2');
  const [instrumentStored, setInstrumentStored] = useState(false);
  // TDI-483: the family decides whether the clinical team sees the profile.
  // null until the record lands; false shows the not-shared state and asks
  // the backend for nothing.
  const [profileShared, setProfileShared] = useState(null);

  // The child's pronoun set drives the static copy of the redesigned report
  // (the AI narratives arrive personalised from the backend with the same set).
  // A sequence counter keeps overlapping async resolutions ordered (a stale
  // slow lookup must never overwrite a newer result), and once resolved the
  // guardian-form fallback is not refetched on later snapshots.
  const [pronounSet, setPronounSet] = useState('they/them');
  const pronounSeqRef = useRef(0);
  const pronounResolvedRef = useRef(false);

  // Keep the child's first name in sync for the report headings.
  useEffect(() => {
    if (!orgId || !patientRecordId) return undefined;
    const ref = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
    let cancelled = false;
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};
      const first = (data?.subject?.firstName || '').trim();
      if (first) setChildName(first);
      setHfpV3Scoring(data?.domains?.hfpV3?.scoring || null);
      setInstrument(hfpInstrumentForRecord(data));
      setInstrumentStored(hasStoredHfpInstrument(data));
      setProfileShared(profileSharedWithClinicians(data));
      setRecordLoaded(true);
      const hasRecordPronouns = Boolean(data?.subject?.preferredPronouns || data?.subject?.pronouns);
      if (hasRecordPronouns || !pronounResolvedRef.current) {
        const seq = ++pronounSeqRef.current;
        resolvePronounSet(orgId, patientRecordId, data).then((set) => {
          if (cancelled || seq !== pronounSeqRef.current) return;
          pronounResolvedRef.current = true;
          setPronounSet(set);
        });
      }
    }, () => setRecordLoaded(true));
    return () => {
      cancelled = true;
      unsub();
    };
  }, [orgId, patientRecordId]);

  const loadReport = useCallback(async (force = false) => {
    if (!orgId || !patientRecordId) return;
    setLoading(true);
    setError(null);
    try {
      // Many LLM calls; override the default 70s client timeout.
      const fn = httpsCallable(functions, 'rmsGenerateHfpV2Profile', { timeout: 540000 });
      const res = await fn({ orgId, patientRecordId, mode: 'llm', force });
      const content = res?.data?.report || null;
      if (content) {
        setReport(content);
      } else if (res?.data?.status === 'not_ready') {
        setError('The profile is not ready yet — both respondents must complete all HFP forms.');
      } else {
        setError('No report was returned. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to load the HFP profile.');
    } finally {
      setLoading(false);
    }
  }, [orgId, patientRecordId]);

  // HFP v3 narratives: the same writer/editor chain as the v2 report, rebound
  // to v3 (rmsGenerateHfpV3Report). The deterministic report renders straight
  // away; the AI paragraphs merge in when the callable returns (cached after
  // the first run for unchanged inputs).
  // narrativesState mirrors the callable's typed statuses:
  //   'loading'   — request in flight (or not yet made)
  //   'ready'     — a narratives payload arrived (generated / cached / with errors)
  //   'not_ready' — the backend reports the report is not ready to generate yet
  //   'error'     — the call failed or returned nothing usable
  const [v3Narratives, setV3Narratives] = useState(null);
  const [v3NarrativesState, setV3NarrativesState] = useState('loading');
  const loadV3Narratives = useCallback(async () => {
    if (!orgId || !patientRecordId) return;
    setV3NarrativesState('loading');
    try {
      const fn = httpsCallable(functions, 'rmsGenerateHfpV3Report', { timeout: 540000 });
      const res = await fn({ orgId, patientRecordId, mode: 'llm' });
      if (res?.data?.narratives) {
        setV3Narratives(res.data.narratives);
        setV3NarrativesState('ready');
      } else if (res?.data?.status === 'not_ready') {
        setV3NarrativesState('not_ready');
      } else {
        setV3NarrativesState('error');
      }
    } catch (err) {
      // Narratives are additive: the deterministic report stands on failure.
      console.error('HFP v3 narratives failed:', err);
      setV3NarrativesState('error');
    }
  }, [orgId, patientRecordId]);

  // Request the narratives once per page view: later record snapshots (status
  // changes, the other informant submitting) must not re-fire the long LLM
  // call or wipe a visible error state mid-read. Retry stays explicit.
  const narrativesRequestedRef = useRef(false);

  // HFP v4 (TDI-526): the clinician view is read from the stored report
  // record, never computed here. 'loading' | 'ready' | 'not_ready' | 'error'.
  const [v4View, setV4View] = useState(null);
  const [v4Child, setV4Child] = useState(null);
  const [v4State, setV4State] = useState('loading');
  const v4RequestedRef = useRef(false);
  const loadV4ClinicianView = useCallback(async () => {
    if (!orgId || !patientRecordId) return;
    setV4State('loading');
    try {
      const res = await apiGetHfpV4Report({ orgId, patientRecordId });
      if (res && res.status === 'ready' && res.report) {
        setV4View(res.report);
        setV4Child(res.child || null);
        setV4State('ready');
      } else {
        setV4State('not_ready');
      }
    } catch (err) {
      console.error('HFP v4 clinician view failed:', err);
      setV4State('error');
    }
  }, [orgId, patientRecordId]);
  // A record with a stored instrument only renders its own generation; records
  // created before the setting render v3 whenever v3 scoring is present. Keep
  // in step with RmsServiceUserHfpPage.
  const showV3 = Boolean(hfpV3Scoring) && (!instrumentStored || instrument === 'v3');
  // The v2 report callable only runs for v2 records not shown as v3.
  useEffect(() => {
    if (!recordLoaded) return;
    if (profileShared === false) {
      // Nothing is requested: the backend would refuse, and the screen says
      // why instead (TDI-483).
      setLoading(false);
      return;
    }
    if (showV3) {
      setLoading(false);
      if (!narrativesRequestedRef.current) {
        narrativesRequestedRef.current = true;
        loadV3Narratives();
      }
      return;
    }
    // v4 (TDI-526): the clinician view of the stored report record, levels
    // only and no AI text, from rmsGetHfpV4Report. Asked for once per page view.
    if (instrument === 'v4') {
      setLoading(false);
      if (!v4RequestedRef.current) {
        v4RequestedRef.current = true;
        loadV4ClinicianView();
      }
      return;
    }
    // Records on v3 (Harbour pupils by default) are not ready without their
    // own scoring. Never fall back to building a v2 report for them.
    if (instrument !== 'v2') {
      setLoading(false);
      setError('The profile is not ready yet — it appears once all forms are complete.');
      return;
    }
    loadReport(false);
  }, [recordLoaded, showV3, instrument, profileShared, loadReport, loadV3Narratives, loadV4ClinicianView]);

  const breadcrumbs = [
    { label: 'Home', path: '/rms/provider/dashboard' },
    { label: 'Patient Overview', path: `/rms/provider/patient/${patientRecordId}` },
    { label: 'Holistic Functional Profile', path: null },
  ];

  // v3: the redesigned report experience owns the whole screen, exactly as the
  // family sees it — its own app bar carries the Back control (here routed to
  // the patient record rather than the service-user overview), and the legal
  // notice rides at the foot of every page inside the app. The provider shell
  // is deliberately not wrapped around it: a second top bar and a second Back
  // would be a clinician-only difference in a view whose whole purpose is to
  // be identical to the family's.
  const v3Model = useMemo(
    () => (showV3 ? buildHfpV3ReportModel(hfpV3Scoring, childName, v3Narratives) : null),
    [showV3, hfpV3Scoring, childName, v3Narratives]
  );

  // TDI-483: with no yes from the family, no profile yet, or SEND consent
  // withdrawn, the page says so and shows nothing of the profile.
  if (recordLoaded && profileShared === false) {
    return (
      <div className="h-screen flex flex-col bg-gradient-to-br from-primary/5 via-white to-secondary/15">
        <RmsTopNavBar breadcrumbs={breadcrumbs} />
        <div className="flex-1 min-h-0 px-6 sm:px-8 lg:px-12 py-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center" role="status">
            <h2 className="text-lg font-semibold text-gray-900">Strengths and needs profile: not shared</h2>
            <p className="text-sm text-gray-600 mt-2">{PROFILE_NOT_SHARED_LINE}</p>
          </div>
        </div>
      </div>
    );
  }

  if (recordLoaded && instrument === 'v4' && profileShared !== false) {
    return (
      <div className="h-screen flex flex-col bg-gradient-to-br from-primary/5 via-white to-secondary/15">
        <RmsTopNavBar breadcrumbs={breadcrumbs} />
        <div className="flex-1 min-h-0 px-6 sm:px-8 lg:px-12 py-4 overflow-hidden">
          <div className="h-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
            {v4State === 'loading' && (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-500 p-8 text-center" role="status">
                Loading the profile.
              </div>
            )}
            {v4State === 'not_ready' && (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-600 p-8 text-center" role="status">
                The profile is not ready yet. It appears once both forms are in and the report has been built.
              </div>
            )}
            {v4State === 'error' && (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 text-sm text-red-600 p-8 text-center" role="alert">
                <span>The profile could not be loaded.</span>
                <button type="button" onClick={loadV4ClinicianView} className="px-3 py-2 rounded-lg border border-gray-300 text-gray-800">
                  Try again
                </button>
              </div>
            )}
            {v4State === 'ready' && (
              <div className="flex-1 min-h-0">
                <HfpV4ClinicianView
                  view={v4View}
                  child={v4Child}
                  onBack={() => navigate(`/rms/provider/patient/${patientRecordId}`)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (showV3) {
    return (
      <div className="h-screen">
        <HfpReportApp
          model={v3Model}
          childName={childName}
          pronounSet={pronounSet}
          narrativesState={v3NarrativesState}
          onRetryNarratives={loadV3Narratives}
          onBack={() => navigate(`/rms/provider/patient/${patientRecordId}`)}
        />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-primary/5 via-white to-secondary/15">
      <RmsTopNavBar breadcrumbs={breadcrumbs} />

      {/* Body: only this region scrolls; the report keeps its sidebar/key sticky. */}
      <div className="flex-1 min-h-0 px-6 sm:px-8 lg:px-12 py-4 overflow-hidden">
        <div className="h-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
          {loading && !report && (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-500 p-8 text-center">
              Building the profile — this can take a minute or two the first time.
            </div>
          )}
          {error && !report && (
            <div className="flex-1 flex items-center justify-center text-sm text-red-600 p-8 text-center">
              {error}
            </div>
          )}
          {report && (
            <div className="flex-1 min-h-0">
              <HfpV2Report report={report} childName={childName} pageMode clinicianView />
            </div>
          )}
        </div>
      </div>

      {/* Navigation footer + disclaimer (always visible) */}
      <footer className="flex-shrink-0 border-t border-gray-200 bg-white px-6 sm:px-8 lg:px-12 py-3">
        <p className="text-[11px] leading-relaxed text-gray-500 text-center max-w-4xl mx-auto">
          This is not a medical assessment and does not constitute a diagnosis, and cannot be
          used to claim or deny any medical condition.
        </p>
      </footer>
    </div>
  );
}
